import { DateTime } from 'luxon'

import { AI_GUIDE_NAME } from '@/client/services/ai/constants'
import type {
  Conversation as HasuraConversation,
  Series as HasuraSeries,
  Series_Host as HasuraSeriesHost,
  Session as HasuraSession,
  Session_Host as HasuraSessionHost,
  User as HasuraUser,
} from '@/client/services/hasura/generated/graphql'

import type {
  HostUserDetails,
  SeriesDetails,
  SessionDetails,
  UserDetails,
} from '@/components/Worlds/types'

import type { ConversationType } from './types'

type AcceptedConversationType = (
  | {
      type: ConversationType
      createdAt: DateTime
    }
  | Pick<HasuraConversation, 'type' | 'created_at'>
) & {
  series:
    | ((Pick<SeriesDetails, 'title'> | Pick<HasuraSeries, 'title'>) &
        (
          | {
              hosts: Pick<HostUserDetails, 'id' | 'displayName' | 'role'>[]
            }
          | {
              series_hosts: (Pick<HasuraSeriesHost, 'role'> & {
                user: Pick<HasuraUser, 'id' | 'virtual_display_name'>
              })[]
            }
        ))
    | null
  session:
    | ((Pick<SessionDetails, 'title'> | Pick<HasuraSession, 'title'>) &
        (
          | {
              hosts: Pick<HostUserDetails, 'id' | 'displayName' | 'role'>[]
            }
          | {
              session_hosts: (Pick<HasuraSessionHost, 'role'> & {
                user: Pick<HasuraUser, 'id' | 'virtual_display_name'>
              })[]
            }
        ))
    | null
} & (
    | {
        users: Pick<UserDetails, 'id' | 'displayName'>[]
      }
    | {
        conversation_users: { user: Pick<HasuraUser, 'id' | 'virtual_display_name'> }[]
      }
  )

function getDisplayName({
  displayName,
  virtual_display_name,
}: {
  virtual_display_name?: string | null | undefined
  displayName?: string | null | undefined
} = {}): string {
  return displayName ?? virtual_display_name ?? ''
}

const SERIES_DIRECT_TUTOR_MIGRATION_DATE = DateTime.utc(2023, 8, 21)

const CONVERSATION_TYPE_TO_TITLE: Record<
  ConversationType,
  ({ conversation, userId }: { conversation: AcceptedConversationType; userId: number }) => string
> = {
  direct: ({ conversation, userId }) => {
    const allUsers =
      'conversation_users' in conversation
        ? conversation.conversation_users.map(item => item.user)
        : conversation.users

    const otherUser = allUsers.find(user => user.id !== userId)

    const currentUser = allUsers.find(user => user.id === userId)

    return getDisplayName(otherUser) ?? getDisplayName(currentUser) ?? 'Empty Chat'
  },

  'one-on-one-request': ({ conversation, userId }) => {
    const allUsers =
      'conversation_users' in conversation
        ? conversation.conversation_users.map(item => item.user)
        : conversation.users

    const otherUser = allUsers.find(user => user.id !== userId)

    const otherUserDisplayName = getDisplayName(otherUser) ?? 'Unknown User'

    return `1:1 Request: ${otherUserDisplayName}`
  },

  'series-group': ({ conversation }) => conversation.series?.title ?? 'Deleted Series',

  'series-direct': ({ conversation }) => {
    const { series } = conversation
    const createdAt =
      'createdAt' in conversation
        ? conversation.createdAt
        : DateTime.fromISO(conversation.created_at)

    if (series == null || createdAt <= SERIES_DIRECT_TUTOR_MIGRATION_DATE) {
      return 'Chat'
    }

    const { title } = series

    const allUsers =
      'conversation_users' in conversation
        ? conversation.conversation_users.map(item => item.user)
        : conversation.users
    const hosts =
      'series_hosts' in series ? series.series_hosts.map(item => item.user) : series.hosts

    const nonHostUsers = allUsers.filter(user => !hosts.some(host => host?.id === user.id))

    if (nonHostUsers.length === 0) {
      return `${title}`
    }

    return `${getDisplayName(nonHostUsers[0])}: ${title}`
  },

  'series-tutors': ({ conversation }) =>
    `Tutors: ${conversation.series?.title ?? 'Deleted Series'}`,

  'series-ai': ({ conversation }) =>
    `Your ${AI_GUIDE_NAME}: ${conversation.series?.title ?? 'Deleted Series'}`,
  'ai-guide': () => AI_GUIDE_NAME,

  'session-direct': ({ conversation }) => {
    const { session } = conversation
    if (session == null) {
      return 'Chat'
    }

    const allUsers =
      'conversation_users' in conversation
        ? conversation.conversation_users.map(item => item.user)
        : conversation.users
    const hosts =
      'session_hosts' in session ? session.session_hosts.map(item => item.user) : session.hosts
    const nonHostUsers = allUsers.filter(user => !hosts.some(host => host.id === user.id))

    if (nonHostUsers.length === 0) {
      return 'Empty Chat'
    }

    return `${getDisplayName(nonHostUsers[0])}: ${session.title}`
  },

  'session-group': ({ conversation }) => conversation.session?.title ?? 'Deleted Session',

  'session-tutors': ({ conversation }) =>
    `Tutors: ${conversation.session?.title ?? 'Deleted Session'}`,

  'session-request': () => 'Session Request Chat',
}

export default function getConversationTitle({
  conversation,
  userId,
}: {
  conversation: AcceptedConversationType
  userId: number
}): string {
  return CONVERSATION_TYPE_TO_TITLE[conversation.type as ConversationType]({ conversation, userId })
}
